import React, { memo, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import Avatar from './Avatar';
import { useLocale } from '../i18n/LocaleContext';
import { celebrate } from '../utils/fx';
import { refreshDuel, subscribeDuel } from '../utils/duelStore';

/**
 * Итог дуэли поверх любого экрана.
 *
 * Матч заканчивается по таймеру, а не по нажатию: чаще всего это застаёт
 * игрока на главной, где он тапает, или в магазине. Раньше результат ждал
 * его на экране дуэлей — то есть проигрыш или победа могли пролежать
 * незамеченными часами. Слой висит в общем макете и показывает итог там,
 * где игрок находится.
 */

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtNum = (v) => {
  const n = num(v);
  if (n >= 100000) return `${Math.round(n / 1000)}k`;
  if (n >= 1000) return n.toFixed(0);
  return n.toFixed(n >= 100 ? 0 : n >= 10 ? 1 : 2);
};

const haptic = (kind) => {
  try {
    window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred(kind);
  } catch (_) {
    /* вне Telegram просто нет вибрации */
  }
};

const ResultModal = ({ row, onClose, onRematch, t }) => {
  const [barIn, setBarIn] = useState(false);
  useEffect(() => {
    if (!row) return undefined;
    if (row.outcome === 'win') celebrate('level');
    haptic(row.outcome === 'win' ? 'success' : row.outcome === 'lose' ? 'error' : 'warning');
    const id = setTimeout(() => setBarIn(true), 220);
    return () => {
      clearTimeout(id);
      setBarIn(false);
    };
  }, [row]);

  if (!row) return null;
  const title =
    row.outcome === 'win' ? t('duelWin') : row.outcome === 'lose' ? t('duelLose') : t('duelDraw');
  // Показываем ровно то, что уже зачислено на баланс: выплата победителю
  // включает бонус за серию, и раньше окно занижало сумму до голой ставки.
  const profit = num(row.profit ?? (row.outcome === 'win' ? row.stake_amount : 0));
  const payout =
    row.outcome === 'win'
      ? `+${fmtNum(profit)}`
      : row.outcome === 'lose'
        ? `−${fmtNum(Math.abs(profit) || num(row.stake_amount))}`
        : t('duelRefunded');
  const after = row.rating_before + row.rating_delta;

  return (
    <div className="duel-result-backdrop" role="dialog" aria-modal="true">
      <div className={`duel-result soft-card outcome-${row.outcome}`}>
        <span className="duel-result-rays" aria-hidden />
        <div className="duel-result-glow" aria-hidden />
        <div className="duel-result-title">{title}</div>

        <div className="duel-result-scores">
          <div className="duel-result-side">
            <Avatar
              userData={{ photo_url: row.me?.photo_url, first_name: row.me?.name }}
              size={54}
            />
            <b>{fmtNum(row.me.score)}</b>
          </div>
          <span className="duel-result-dash">:</span>
          <div className="duel-result-side">
            <Avatar
              userData={{ photo_url: row.foe?.photo_url, first_name: row.foe?.name }}
              size={54}
            />
            <b>{fmtNum(row.foe.score)}</b>
          </div>
        </div>

        <div className="duel-result-rows">
          <div className="duel-result-row">
            <span>{row.outcome === 'win' ? t('duelCredited') : t('duelStake')}</span>
            <b className={`outcome-${row.outcome}`}>{payout}</b>
          </div>
          {row.is_ranked ? (
            <>
              <div className="duel-result-row">
                <span>{t('duelRating')}</span>
                <b className={row.rating_delta >= 0 ? 'outcome-win' : 'outcome-lose'}>
                  {row.rating_delta > 0 ? `+${row.rating_delta}` : row.rating_delta}
                  <i className="duel-result-after"> → {after}</i>
                </b>
              </div>
              <div className="duel-result-bar" aria-hidden>
                <i
                  className={row.rating_delta >= 0 ? 'up' : 'down'}
                  style={{ width: barIn ? `${Math.min(100, Math.abs(row.rating_delta) * 3)}%` : 0 }}
                />
              </div>
            </>
          ) : (
            <div className="duel-result-row">
              <span>{t('duelRating')}</span>
              <b>{t('duelCasual')}</b>
            </div>
          )}
        </div>

        <div className="duel-result-actions">
          <button type="button" className="action-btn soft-btn" onClick={onClose}>
            {t('close')}
          </button>
          {row.foe?.id ? (
            <button type="button" className="action-btn" onClick={() => onRematch(row)}>
              {t('duelRematch')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};


/** Memo: it takes no props, so it never needs to follow an App-level re-render. */
const DuelResultLayer = memo(function DuelResultLayer() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [row, setRow] = useState(null);
  const [busy, setBusy] = useState(false);
  // Закрытые итоги. Сервер узнаёт о просмотре асинхронно, и до следующего
  // опроса тот же матч ещё висит в `unseen_results`. Без этого списка эффект
  // открывал окно заново сразу после закрытия — со стороны выглядело так,
  // будто кнопку нужно нажимать дважды.
  const dismissed = useRef(new Set());

  useEffect(() => subscribeDuel((next) => setState(next)), []);

  useEffect(() => {
    if (row) return;
    const next = (state?.unseen_results || []).find((r) => !dismissed.current.has(r.id));
    if (next) setRow(next);
  }, [state, row]);

  const markSeen = (current) => {
    if (!current?.id) return;
    dismissed.current.add(current.id);
    api.duelSeen(current.id).catch(() => {
      /* итог всё равно остаётся в истории боёв */
    });
  };

  const close = () => {
    const current = row;
    setRow(null);
    markSeen(current);
    refreshDuel();
  };

  const rematch = async () => {
    const current = row;
    setRow(null);
    markSeen(current);
    if (!current?.id || busy) return;
    setBusy(true);
    try {
      await api.duelRematch(current.id);
      await refreshDuel({ silent: false });
    } catch (_) {
      /* экран дуэлей покажет причину отказа */
    } finally {
      setBusy(false);
      navigate('/clicker/duel');
    }
  };

  return <ResultModal row={row} onClose={close} onRematch={rematch} t={t} />;
});

export default DuelResultLayer;
